/**
 * Industry Library Page
 * Lists all industries with search, filter, and generation capabilities
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Filter,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Building2,
  BarChart3,
  RefreshCw,
  ArrowUpDown,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

import { listIndustries, deleteIndustry } from '../lib/api/industryResearchApi';
import { SESSION_CACHE } from '../lib/queryCache';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { useAppStore } from '../stores/appStore';
import type {
  IndustrySummary,
  IndustryStatus,
  DataRefreshStatus,
  IndustryFilters,
} from '../types/industryResearch';
import {
  STATUS_COLORS,
  DATA_REFRESH_STATUS_COLORS,
  DATA_REFRESH_STATUS_LABELS,
  getDataAge,
  getFreshnessColor,
} from '../types/industryResearch';

const IndustryLibrary: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUser = useAppStore((state) => state.currentUser);
  const [filters, setFilters] = useState<IndustryFilters>({
    search: '',
    status: undefined,
    category: undefined,
    dataRefreshStatus: undefined,
    sortBy: 'updated',
    sortOrder: 'desc',
  });

  // Fetch industries
  const {
    data: industries,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['industries', filters.status, filters.category],
    queryFn: () =>
      listIndustries(filters.status, filters.category),
    ...SESSION_CACHE,
  });

  // Delete (soft-delete → archive) mutation
  const deleteMutation = useMutation({
    mutationFn: (industryId: string) =>
      deleteIndustry(industryId, currentUser?.email || currentUser?.id || 'system'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['industries'] });
      toast.success('Industry deleted');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to delete industry');
    },
  });

  // Themed-confirm flow. We hold the industry that's pending deletion in
  // state; rendering the dialog at the bottom of the page is cheaper than
  // dropping one per card. Stops native window.confirm() from popping up.
  const [pendingDelete, setPendingDelete] = useState<IndustrySummary | null>(null);

  const handleDeleteIndustry = (
    e: React.MouseEvent,
    industry: IndustrySummary
  ) => {
    e.stopPropagation(); // Prevent card click → navigate to details
    setPendingDelete(industry);
  };

  // Filter and sort locally
  const filteredIndustries = useMemo(() => {
    if (!industries) return [];

    let filtered = [...industries];

    // Search filter (local)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (industry) =>
          industry.name.toLowerCase().includes(searchLower) ||
          industry.displayName.toLowerCase().includes(searchLower) ||
          industry.category.toLowerCase().includes(searchLower)
      );
    }

    // Data refresh status filter
    if (filters.dataRefreshStatus) {
      filtered = filtered.filter(
        (industry) => industry.dataRefreshStatus === filters.dataRefreshStatus
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: any, bVal: any;

      switch (filters.sortBy) {
        case 'name':
          aVal = a.displayName;
          bVal = b.displayName;
          break;
        case 'created':
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
          break;
        case 'cases':
          aVal = a.activeCaseCount;
          bVal = b.activeCaseCount;
          break;
        case 'updated':
        default:
          aVal = a.lastDataRefreshAt
            ? new Date(a.lastDataRefreshAt).getTime()
            : 0;
          bVal = b.lastDataRefreshAt
            ? new Date(b.lastDataRefreshAt).getTime()
            : 0;
          break;
      }

      if (filters.sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  }, [industries, filters]);

  const handleAddNew = () => {
    navigate('/industries/generate');
  };

  const handleViewDetails = (industryId: string) => {
    navigate(`/industries/${industryId}`);
  };

  const getStatusIcon = (status: IndustryStatus) => {
    switch (status) {
      case 'active':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'emerging':
        return <TrendingUp className="w-4 h-4" />;
      case 'declining':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Building2 className="w-4 h-4" />;
    }
  };

  const getDataRefreshIcon = (status: DataRefreshStatus) => {
    switch (status) {
      case 'current':
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-orange-600" />;
      case 'stale':
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case 'failed':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
    }
  };

  const formatDataAge = (lastRefreshAt?: string): string => {
    if (!lastRefreshAt) return 'Never researched';
    const age = getDataAge(lastRefreshAt);
    if (age === null) return 'Never';
    if (age === 0) return 'Today';
    if (age === 1) return '1 day ago';
    if (age < 30) return `${age} days ago`;
    if (age < 90) return `${Math.floor(age / 7)} weeks ago`;
    return `${Math.floor(age / 30)} months ago`;
  };

  return (
    <div className="p-8 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900 flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-primary-600" />
              Industry Research
            </h1>
            <p className="text-neutral-600 mt-2">
              Comprehensive industry intelligence with versioned research and
              market analysis
            </p>
          </div>
          <button
            onClick={handleAddNew}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add New Industry
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex gap-4 flex-wrap">
          {/* Search */}
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                placeholder="Search industries..."
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
                className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={filters.status || ''}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                status: e.target.value
                  ? (e.target.value as IndustryStatus)
                  : undefined,
              }))
            }
            className="px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="emerging">Emerging</option>
            <option value="mature">Mature</option>
            <option value="declining">Declining</option>
          </select>

          {/* Data Refresh Status Filter */}
          <select
            value={filters.dataRefreshStatus || ''}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                dataRefreshStatus: e.target.value
                  ? (e.target.value as DataRefreshStatus)
                  : undefined,
              }))
            }
            className="px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Data Status</option>
            <option value="current">Current</option>
            <option value="stale">Stale</option>
            <option value="pending">Pending Research</option>
            <option value="failed">Failed</option>
          </select>

          {/* Sort */}
          <select
            value={`${filters.sortBy}-${filters.sortOrder}`}
            onChange={(e) => {
              const [sortBy, sortOrder] = e.target.value.split('-') as [
                IndustryFilters['sortBy'],
                IndustryFilters['sortOrder']
              ];
              setFilters((prev) => ({ ...prev, sortBy, sortOrder }));
            }}
            className="px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="updated-desc">Recently Updated</option>
            <option value="updated-asc">Oldest Updated</option>
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
            <option value="created-desc">Recently Created</option>
            <option value="cases-desc">Most Cases</option>
          </select>

          {/* Refresh */}
          <button
            onClick={() => refetch()}
            className="px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      {industries && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <div className="text-sm text-neutral-600">Total Industries</div>
            <div className="text-2xl font-bold text-neutral-900">
              {industries.length}
            </div>
          </div>
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <div className="text-sm text-neutral-600">Active</div>
            <div className="text-2xl font-bold text-green-600">
              {
                industries.filter((i) => i.status === 'active' || i.status === 'emerging')
                  .length
              }
            </div>
          </div>
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <div className="text-sm text-neutral-600">Current Data</div>
            <div className="text-2xl font-bold text-blue-600">
              {
                industries.filter((i) => i.dataRefreshStatus === 'current')
                  .length
              }
            </div>
          </div>
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <div className="text-sm text-neutral-600">Pending Research</div>
            <div className="text-2xl font-bold text-orange-600">
              {
                industries.filter((i) => i.dataRefreshStatus === 'pending')
                  .length
              }
            </div>
          </div>
        </div>
      )}

      {/* Loading State — Skeleton Cards */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white border border-neutral-200 rounded-lg p-6 animate-pulse">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="h-5 bg-neutral-200 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-neutral-100 rounded w-1/2" />
                </div>
                <div className="h-6 w-16 bg-neutral-200 rounded-full" />
              </div>
              <div className="space-y-3 mb-4">
                <div className="flex justify-between">
                  <div className="h-3 bg-neutral-100 rounded w-1/3" />
                  <div className="h-3 bg-neutral-100 rounded w-1/4" />
                </div>
                <div className="flex justify-between">
                  <div className="h-3 bg-neutral-100 rounded w-2/5" />
                  <div className="h-3 bg-neutral-100 rounded w-1/5" />
                </div>
              </div>
              <div className="pt-4 border-t border-neutral-100 flex justify-between">
                <div className="h-3 bg-neutral-100 rounded w-1/3" />
                <div className="h-3 bg-neutral-100 rounded w-1/4" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">
            Failed to load industries
          </h3>
          <p className="text-red-700 mb-4">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && filteredIndustries.length === 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-12 text-center">
          <TrendingUp className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-neutral-900 mb-2">
            No industries found
          </h3>
          <p className="text-neutral-600 mb-6">
            {filters.search || filters.status || filters.dataRefreshStatus
              ? 'Try adjusting your filters'
              : 'Get started by adding your first industry'}
          </p>
          {!filters.search && !filters.status && !filters.dataRefreshStatus && (
            <button
              onClick={handleAddNew}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Plus className="w-5 h-5 inline mr-2" />
              Add New Industry
            </button>
          )}
        </div>
      )}

      {/* Industry Grid */}
      {!isLoading && !error && filteredIndustries.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredIndustries.map((industry) => {
            const dataAge = getDataAge(industry.lastDataRefreshAt);
            const freshnessColor = getFreshnessColor(dataAge);

            return (
              <div
                key={industry.industryId}
                className="bg-white border border-neutral-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleViewDetails(industry.industryId)}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-neutral-900 mb-1">
                      {industry.displayName}
                    </h3>
                    <p className="text-sm text-neutral-600">
                      {industry.category}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {/* Status Badge */}
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        STATUS_COLORS[industry.status]
                      }`}
                    >
                      {getStatusIcon(industry.status)}
                      {industry.status}
                    </span>
                  </div>
                </div>

                {/* Data Freshness */}
                <div className="mb-4">
                  <div
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
                      DATA_REFRESH_STATUS_COLORS[industry.dataRefreshStatus]
                    }`}
                  >
                    {getDataRefreshIcon(industry.dataRefreshStatus)}
                    <span>
                      {DATA_REFRESH_STATUS_LABELS[industry.dataRefreshStatus]}
                    </span>
                  </div>
                  {industry.lastDataRefreshAt && (
                    <div className={`text-xs mt-2 ${freshnessColor}`}>
                      Updated {formatDataAge(industry.lastDataRefreshAt)}
                    </div>
                  )}
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-2 gap-4 mb-4 pt-4 border-t border-neutral-200">
                  <div>
                    <div className="text-xs text-neutral-600">Version</div>
                    <div className="text-sm font-semibold text-neutral-900">
                      {industry.currentVersion || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-600">Cases</div>
                    <div className="text-sm font-semibold text-neutral-900 flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      {industry.activeCaseCount}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-600">Versions</div>
                    <div className="text-sm font-semibold text-neutral-900 flex items-center gap-1">
                      <BarChart3 className="w-3 h-3" />
                      {industry.totalVersions}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-600">Stage</div>
                    <div className="text-sm font-semibold text-neutral-900">
                      {industry.maturityStage || 'N/A'}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails(industry.industryId);
                    }}
                    className="flex-1 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
                  >
                    View Details
                  </button>
                  {industry.dataRefreshStatus === 'pending' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          `/industries/${industry.industryId}?action=generate`
                        );
                      }}
                      className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
                    >
                      Generate
                    </button>
                  )}
                  <button
                    onClick={(e) => handleDeleteIndustry(e, industry)}
                    disabled={
                      deleteMutation.isPending &&
                      deleteMutation.variables === industry.industryId
                    }
                    title="Delete industry"
                    aria-label={`Delete ${industry.displayName}`}
                    className="px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 hover:border-red-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Themed delete-confirm. Replaces window.confirm() to match the
          rest of the app's UI. Holds a single dialog instance for the
          list and rebinds it based on which industry the user picked. */}
      <ConfirmDialog
        isOpen={pendingDelete !== null}
        title={`Delete "${pendingDelete?.displayName ?? 'industry'}"?`}
        message={[
          'The industry will be archived.',
          'Historical versions and case snapshots are preserved.',
        ]}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteMutation.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return;
          deleteMutation.mutate(pendingDelete.industryId, {
            onSettled: () => setPendingDelete(null),
          });
        }}
      />
    </div>
  );
};

export default IndustryLibrary;
